import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as AudioAiIcon } from "../../../Assets/icon/Audio-ai.svg";
import { ReactComponent as XGreyIcon } from "../../../Assets/icon/X-grey.svg";
import { markElementViewed } from "../../../Services/elementsOverview";
import { getShowingUpRecently, getTryDifferentApproach } from "../../../Services/elements";
import "./style.css";

// Map an element's domain key to the API spec key (Judgement → Judgment, Motivation → Motives).
const DOMAIN_KEY_NORMALIZE = {
  Identity: "Identity",
  Cognition: "Cognition",
  Judgement: "Judgment",
  Judgment: "Judgment",
  Resolution: "Resolution",
  Motivation: "Motives",
};

// `content.levelDescription` arrives as one HTML blob with the lead paragraph,
// then `<b>Strength:</b> ...`, then `<b>Potential risk:</b> ...` (sometimes
// "Potential Risk" with a capital R). Split on the bold labels so the modal
// can show three discrete sections that match the Figma.
const parseLevelDescription = (raw) => {
  if (!raw || typeof raw !== "string") return { howThisShowsUp: "", strength: "", watchFor: "" };

  const html = raw
    .replace(/<br\s*\/?>(\s*<br\s*\/?>)*/gi, "<br/>") // normalize <br>
    .replace(/&nbsp;/g, " ");

  // Split on the bold-label boundaries. The regex captures the label name
  // so we can route each segment into the right bucket.
  const parts = html.split(/<b>\s*(Strength|Potential\s*Risk|Potential\s*risk)\s*:?\s*<\/b>/i);

  // parts[0] is the lead paragraph. After that the array alternates:
  // [label, segment, label, segment, ...]
  const sections = { howThisShowsUp: (parts[0] || "").trim(), strength: "", watchFor: "" };

  for (let i = 1; i < parts.length; i += 2) {
    const label = (parts[i] || "").toLowerCase();
    const body = (parts[i + 1] || "").trim();
    if (label.startsWith("strength")) sections.strength = body;
    else if (label.startsWith("potential")) sections.watchFor = body;
  }

  // Trim trailing/leading <br/> noise from each section.
  const cleanup = (s) => s.replace(/^(<br\/>\s*)+/i, "").replace(/(\s*<br\/>)+$/i, "").trim();
  sections.howThisShowsUp = cleanup(sections.howThisShowsUp);
  sections.strength = cleanup(sections.strength);
  sections.watchFor = cleanup(sections.watchFor);
  return sections;
};

// The Recent / Grow card content arrives as a single plain-text blob with
// blank-line-separated sections. Each section's first line is the section
// title, and the body may contain an "intro" line ending in `:`, dash-prefixed
// bullet items, and/or paragraph lines. Convert that into structured sections
// so we can render the multi-block layout from Figma node 14914:17519
// (centered titles, gray intros, plain centered text lines).
const parseCardContent = (raw) => {
  if (!raw || typeof raw !== "string") return [];

  const blocks = raw.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);

  return blocks
    .map((block) => {
      const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
      if (lines.length === 0) return null;

      const section = { title: lines[0], items: [] };
      let i = 1;
      while (i < lines.length) {
        const line = lines[i];
        if (/^[-*•]\s*/.test(line)) {
          // Group consecutive bullet lines into a single list block.
          const bullets = [];
          while (i < lines.length && /^[-*•]\s*/.test(lines[i])) {
            bullets.push(lines[i].replace(/^[-*•]\s*/, ""));
            i++;
          }
          section.items.push({ kind: "list", lines: bullets });
        } else if (line.endsWith(":") && section.items.length === 0) {
          // Only the first ":-terminated" line is treated as the gray intro
          // (matches the Figma where a single intro precedes the bullet list).
          section.items.push({ kind: "intro", text: line });
          i++;
        } else {
          section.items.push({ kind: "para", text: line });
          i++;
        }
      }
      return section;
    })
    .filter(Boolean);
};

// Pull the current coaching mode the user is in. Defaults to "keyboard" so that
// a freshly-installed account lands in chat instead of dialing voice.
const getCoachingMode = () => {
  try {
    const v = (localStorage.getItem("coaching_mode") || "").trim().toLowerCase();
    return v === "voice" ? "voice" : "keyboard";
  } catch (_) {
    return "keyboard";
  }
};

const DOMAIN_SHORT_CODE = {
  Identity: "ID",
  Cognition: "CO",
  Judgement: "JD",
  Judgment: "JD",
  Resolution: "RS",
  Motivation: "MO",
};

const ElementDetailModal = ({ isOpen, onClose, element, preloadedCards }) => {
  const navigate = useNavigate();

  const modalRef = useRef(null);
  const [animationStage, setAnimationStage] = useState("zoom-in");

  // Lazy AI card sections + mark-viewed
  const [showingUpRecently, setShowingUpRecently] = useState(null);
  const [tryDifferentApproach, setTryDifferentApproach] = useState(null);
  const [recentLoading, setRecentLoading] = useState(false);
  const [approachLoading, setApproachLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setAnimationStage("zoom-in");
      setShowingUpRecently(null);
      setTryDifferentApproach(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !element) return;
    const elementKey = element.key || element.element_key || (element.symbol || "").toLowerCase();
    if (!elementKey) return;

    // The API may return the label as either lowercase ("recent"/"grow", as in
    // the v2 elements payload) or already-uppercased ("RECENT"/"GROW"). Normalize
    // before deciding which AI card sections to show.
    const normalizedLabel = String(element.label || "").toLowerCase();

    let cancelled = false;
    markElementViewed(elementKey).catch(() => {});

    if (normalizedLabel === "recent") {
      // Prefer the pre-rendered card from the elements payload — saves a round
      // trip and lets the card render instantly. Fall back to the per-element
      // endpoint only when the bulk payload didn't include it.
      const preRecent = preloadedCards?.showing_up_recently || null;
      if (preRecent && preRecent.content) {
        setShowingUpRecently(preRecent);
        setRecentLoading(false);
      } else {
        setRecentLoading(true);
        getShowingUpRecently(elementKey)
          .then((data) => { if (!cancelled) setShowingUpRecently(data || null); })
          .finally(() => { if (!cancelled) setRecentLoading(false); });
      }
    }

    if (normalizedLabel === "grow") {
      const preApproach = preloadedCards?.try_different_approach || null;
      if (preApproach && preApproach.content) {
        setTryDifferentApproach(preApproach);
        setApproachLoading(false);
      } else {
        setApproachLoading(true);
        getTryDifferentApproach(elementKey)
          .then((data) => { if (!cancelled) setTryDifferentApproach(data || null); })
          .finally(() => { if (!cancelled) setApproachLoading(false); });
      }
    }

    return () => { cancelled = true; };
  }, [isOpen, element, preloadedCards]);

  const handleClose = () => {
    setAnimationStage("zoom-out");
    setTimeout(() => { onClose(); }, 400);
  };

  // Per the Element Coaching & Roleplay contract, an element card always
  // launches a `scope=element` conversation with a `prompt_variant` that
  // mirrors the card label the member tapped (NEW / RECENT / GROW).
  // The synthesized first-message ("question") is shown as the user's
  // chat bubble and becomes the literal `query` the backend sees.
  const ELEMENT_PROMPT_VARIANT_FALLBACK = "new";
  const buildElementQuestion = (topic, variant) => {
    const t = topic || "this";
    switch (variant) {
      case "recent":
        return `Tell me how ${t} has been showing up for me`;
      case "grow":
        return `Help me work with my ${t} element`;
      case "new":
      default:
        return `Tell me about my ${t} element`;
    }
  };

  // Build a navigation URL that ChatInterface (chat) or Coach (voice)
  // consumes to dispatch the right Aris flow.
  const buildHandoffUrl = (action, modality) => {
    // Keyboard-mode chat goes straight to /chat so ChatInterface's
    // auto-start effect can fire on mount; voice goes to /coach/private.
    const basePath = modality === "voice" ? "/coach/private" : "/chat";
    const params = new URLSearchParams();
    params.set("element_action", action);
    params.set("modality", modality);
    if (element?.displayName) params.set("topic", element.displayName);

    const rawDomain = element?.category || element?.domain || "";
    const normalizedDomain = DOMAIN_KEY_NORMALIZE[rawDomain] || rawDomain;
    if (normalizedDomain) params.set("domain", normalizedDomain);
    if (element?.level) params.set("level", element.level);

    if (action === "coaching") {
      // Element-card coaching is always scope=element — single-element
      // conversation. prompt_variant mirrors the card label (NEW/RECENT/
      // GROW). question is the synthesized first user message.
      const variant = (element?.label || ELEMENT_PROMPT_VARIANT_FALLBACK).toLowerCase();
      params.set("scope", "element");
      params.set("prompt_variant", variant);
      const question = buildElementQuestion(element?.displayName, variant);
      params.set("question", question);
      // Existing chat pipeline detection flag.
      params.set("elements", "1");
    } else if (action === "roleplay") {
      const situation = tryDifferentApproach?.content || element?.content?.tryThisInstead || "";
      const reason = element?.content?.whyThisMatters || "";
      if (situation) params.set("situation", situation);
      if (reason) params.set("reason", reason);
      if (element?.relationship) params.set("relationship", element.relationship);
    }

    return `${basePath}?${params.toString()}`;
  };

  const launchAction = (action) => {
    const modality = getCoachingMode() === "voice" ? "voice" : "chat";
    const url = buildHandoffUrl(action, modality);
    onClose();
    navigate(url);
  };

  if (!isOpen || !element) return null;

  const rawDomain = element?.category || element?.domain || "";
  const domainShort = DOMAIN_SHORT_CODE[rawDomain] || (element?.symbol || "").slice(0, 2).toUpperCase();
  const paddedId = String(element?.id ?? "").padStart(2, "0");

  // Pull Strength + Watch For (Potential Risk) out of `levelDescription`'s
  // single HTML blob. Prefer the level-specific lead paragraph for "How
  // This Shows Up" — it's the user-personalized text. Fall back to the
  // generic `definition` only when the level intro isn't there.
  const parsed = parseLevelDescription(element?.content?.levelDescription);
  const howThisShowsUp =
    element?.content?.howThisShowsUp ||
    parsed.howThisShowsUp ||
    element?.content?.definition ||
    "";
  const strength =
    element?.content?.strength ||
    element?.content?.strengths ||
    parsed.strength ||
    "";
  const watchFor =
    element?.content?.watchFor ||
    element?.content?.watchOut ||
    parsed.watchFor ||
    "";

  const label = String(element?.label || "").toLowerCase();
  const isGrow = label === "grow";
  const isRecent = label === "recent";

  const levelLabel = element?.level || "";

  return (
    <div className="element-detail-overlay">
      <div
        ref={modalRef}
        className={`element-detail-modal ${animationStage}`}
      >
        <button
          onClick={handleClose}
          className="element-detail-close"
          aria-label="Close"
        >
          <XGreyIcon className="element-detail-close-icon" aria-hidden="true" />
        </button>

        <div className="element-detail-scroll">
          {/* Hero: black card with ID badge, gem, name + level — matches
              Figma node 14863:77892 (Element Info Card – Default). */}
          <div className="element-hero">
            <div className="element-hero-id">
              <span className="element-hero-id-prefix">{domainShort}</span>
              <span className="element-hero-id-bullet">•</span>
              <span className="element-hero-id-value">{paddedId}</span>
            </div>
            <div className="element-hero-image">
              <img src={element.element_url} alt={element.displayName || element.symbol || ""} />
            </div>
            <h2 className="element-hero-name">{element.displayName}</h2>
            {levelLabel && <p className="element-hero-level">{levelLabel}</p>}
          </div>

          {/* Three default sections */}
          {howThisShowsUp && (
            <div className="element-info-section">
              <h3 className="element-info-section-title">How This Shows Up</h3>
              <p
                className="element-info-section-body"
                dangerouslySetInnerHTML={{ __html: howThisShowsUp }}
              />
            </div>
          )}
          {strength && (
            <>
              <div className="element-info-divider" />
              <div className="element-info-section">
                <h3 className="element-info-section-title">Strength</h3>
                <p
                  className="element-info-section-body"
                  dangerouslySetInnerHTML={{ __html: strength }}
                />
              </div>
            </>
          )}
          {watchFor && (
            <>
              <div className="element-info-divider" />
              <div className="element-info-section">
                <h3 className="element-info-section-title">Watch For</h3>
                <p
                  className="element-info-section-body"
                  dangerouslySetInnerHTML={{ __html: watchFor }}
                />
              </div>
            </>
          )}

          {/* RECENT-only: Showing Up Recently (blue card)
              Matches Figma 14914:17519 styling, recent variant. */}
          {isRecent && (recentLoading || (showingUpRecently && showingUpRecently.content)) && (
            <div className="element-ai-card element-ai-card-recent">
              {recentLoading ? (
                <>
                  <h3 className="element-ai-card-title">Showing Up Recently</h3>
                  <div className="element-ai-skeleton">
                    <span /><span /><span />
                  </div>
                </>
              ) : (
                parseCardContent(showingUpRecently.content).map((section, idx) => (
                  <div key={`recent-${idx}`} className="element-ai-card-section">
                    <h3 className="element-ai-card-title">{section.title}</h3>
                    {section.items.map((item, j) => {
                      if (item.kind === "intro") {
                        return (
                          <p key={j} className="element-ai-card-intro">{item.text}</p>
                        );
                      }
                      if (item.kind === "list") {
                        return (
                          <div key={j} className="element-ai-card-list">
                            {item.lines.map((line, k) => (
                              <p key={k} className="element-ai-card-line">{line}</p>
                            ))}
                          </div>
                        );
                      }
                      return (
                        <p key={j} className="element-ai-card-line">{item.text}</p>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          )}

          {/* GROW-only: Try a Different Approach (green card)
              Matches Figma node 14914:17519 — bg rgba(0,187,155,0.02),
              1px #00bb9b border, centered sections with green titles. */}
          {isGrow && (approachLoading ||
            (tryDifferentApproach && tryDifferentApproach.eligible !== false && tryDifferentApproach.content)) && (
            <div className="element-ai-card element-ai-card-grow">
              {approachLoading ? (
                <>
                  <h3 className="element-ai-card-title">Try a Different Approach</h3>
                  <div className="element-ai-skeleton">
                    <span /><span /><span />
                  </div>
                </>
              ) : (
                parseCardContent(tryDifferentApproach.content).map((section, idx) => (
                  <div key={`grow-${idx}`} className="element-ai-card-section">
                    <h3 className="element-ai-card-title">{section.title}</h3>
                    {section.items.map((item, j) => {
                      if (item.kind === "intro") {
                        return (
                          <p key={j} className="element-ai-card-intro">{item.text}</p>
                        );
                      }
                      if (item.kind === "list") {
                        return (
                          <div key={j} className="element-ai-card-list">
                            {item.lines.map((line, k) => (
                              <p key={k} className="element-ai-card-line">{line}</p>
                            ))}
                          </div>
                        );
                      }
                      return (
                        <p key={j} className="element-ai-card-line">{item.text}</p>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer CTAs (Figma node 14914:17428).
            For GROW elements there are two stacked CTAs, each with its own
            helper line: a white "Practice this Scenario" button followed by
            the blue "Talk it through with Aris" primary CTA. For other
            elements only the primary CTA is shown.
            When the user is in voice coaching mode, the primary CTA swaps
            its icon to the audio-waveform glyph. */}
        <div className="element-detail-footer element-detail-footer-ctas">
          {isGrow && (
            <div className="element-cta-block">
              <button
                type="button"
                className="element-cta element-cta-secondary"
                onClick={() => launchAction("roleplay")}
              >
                Practice this Scenario
              </button>
              <p className="element-detail-cta-helper">
                Try a different approach in a real situation.
              </p>
            </div>
          )}
          <div className="element-cta-block">
            <button
              type="button"
              className="element-cta element-cta-primary"
              onClick={() => launchAction("coaching")}
            >
              <AudioAiIcon className="element-cta-icon" aria-hidden="true" />
              Talk it through with Aris
            </button>
            <p className="element-detail-cta-helper">
              Get a clearer sense of how this shows up for you.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ElementDetailModal;
